import React, { useState, useEffect, useCallback, useMemo } from "react";
import { FiPlus, FiEdit2, FiTrash2, FiArrowUp, FiArrowDown } from "react-icons/fi";
import Modal from "react-modal";
import Select from "react-select";
import { customSelect } from "./Styles/CustomStyles";
import { FaAngleDoubleRight } from "react-icons/fa";
import DynamicBestSellingSection from "./DynamicBestSellingSection";
import CreatableSelect from 'react-select/creatable';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import { createShowcaseSection, updateShowcaseSection, reActiveShowcaseSection, deActiveShowcaseSection, getShowcaseSections, deleteShowcaseSection, getOnlyProductNames, getCategories, updateShowcaseSectionOrder } from "../services/ClientService";
import ConfirmModal from './ConfirmModal';

const ShowcaseSections = () => {

  const textToBg = (textClass) => {
    if (textClass && textClass.startsWith('text-')) {
      // Mapping text colors to appropriate background colors
      const colorMap = {
        "text-white": "bg-gray-200",  // Light gray instead of white to be visible
        "text-gray-800": "bg-gray-300",
        "text-blue-800": "bg-blue-300",
        "text-green-800": "bg-green-300",
        "text-yellow-800": "bg-yellow-300",
        "text-red-800": "bg-red-300",
        "text-purple-800": "bg-purple-300",
        "text-pink-800": "bg-pink-300",
      };

      return colorMap[textClass] || textClass.replace("text-", "bg-");
    }
    return textClass; // Return original class if it doesn't start with 'text-'
  };

  const borderToBg = (borderClass) => {
    if (borderClass && borderClass.startsWith('border-')) {
      const colorMap = {
        "border-gray-200": "bg-gray-200",
        "border-red-500": "bg-red-500",
        "border-green-500": "bg-green-500",
        "border-blue-500": "bg-blue-500",
        "border-yellow-500": "bg-yellow-500",
        "border-pink-500": "bg-pink-500",
        "border-purple-500": "bg-purple-500",
        "border-white": "bg-gray-200", // Change white to light gray for visibility
        "border-transparent": "bg-transparent",
      };

      return colorMap[borderClass] || `bg-${borderClass.split('-').slice(1).join('-')}`;
    }
    return borderClass; // Return original class if it doesn't start with 'border-'
  };

  const priceDetailsLayoutOptions = [
    { value: 'side-by-side', label: 'Side by Side' },
    { value: 'stacked', label: 'Stacked' }
  ];

  const productInfoStyleOptions = [
    { value: 'default', label: 'Default' },
    { value: 'card', label: 'Card' }
  ];

  // =====================================
  // SECTION TYPE OPTIONS - 22 Layout Styles
  // =====================================
  const sectionTypeOptions = [
    { value: 'grid-2col', label: 'Grid 2 Column', icon: '▦', desc: 'Classic 2-column product grid' },
    { value: 'horizontal-scroll', label: 'Horizontal Scroll', icon: '⟷', desc: 'Scrollable horizontal product cards' },
    { value: 'single-highlight', label: 'Single Highlight', icon: '◉', desc: 'One large featured product' },
    { value: 'offer-cards', label: 'Offer Cards', icon: '🏷️', desc: 'Small scrollable offer/deal cards' },
    { value: 'grid-3col', label: 'Grid 3 Column', icon: '▤', desc: 'Compact 3-column grid layout' },
    { value: 'grid-4col', label: 'Grid 4 Column', icon: '▥', desc: 'Compact 4-column grid layout for all screen sizes' },
    { value: 'list-vertical', label: 'Vertical List', icon: '☰', desc: 'Full-width product list cards' },
    { value: 'big-card-carousel', label: 'Big Card Carousel', icon: '🎠', desc: 'Large swipeable product cards' },
    { value: 'category-banner', label: 'Category Banner', icon: '🖼️', desc: 'Wide image banner with overlay text' },
    { value: 'countdown-deals', label: 'Countdown Deals', icon: '⏰', desc: 'Products with urgency timer' },
    { value: 'stacked-cards', label: 'Stacked Cards', icon: '📚', desc: 'Overlapping stacked card design' },
    { value: 'masonry-grid', label: 'Masonry Grid', icon: '🧱', desc: 'Pinterest-style varied height grid' },
    { value: 'spotlight-row', label: 'Spotlight Row', icon: '🔦', desc: '1 big + 2 small products layout' },
    { value: 'mini-cards', label: 'Mini Cards', icon: '📎', desc: 'Tiny compact product chips' },
    { value: 'full-width-banner', label: 'Full Width Banner', icon: '📐', desc: 'Full-screen width product banner' },
    { value: 'circular-showcase', label: 'Circular Showcase', icon: '⭕', desc: 'Round image product bubbles' },
    { value: 'split-screen', label: 'Split Screen', icon: '◧', desc: 'Two products side by side featured' },
    { value: 'story-cards', label: 'Story Cards', icon: '📱', desc: 'Instagram story-style round cards' },
    { value: 'zigzag-list', label: 'Zigzag List', icon: '⚡', desc: 'Alternating left-right layout' },
    { value: 'card-stack-3d', label: '3D Card Stack', icon: '🃏', desc: 'Layered 3D perspective cards' },
    { value: 'brand-strip', label: 'Brand Strip', icon: '🏪', desc: 'Brand logo strip with product counts' },
    { value: 'flash-deals', label: 'Flash Deals', icon: '⚡', desc: 'Bold flash sale card design' },
    { value: 'editorial-grid', label: 'Editorial Grid', icon: '📰', desc: 'Magazine-style editorial layout' },
    // =====================================
    // 20 NEW SECTION LAYOUT TYPES (23-42)
    // =====================================
    { value: 'premium-grid', label: 'Premium Grid', icon: '💎', desc: 'Luxury feel 2-col with gradient cards' },
    { value: 'waterfall-cards', label: 'Waterfall Cards', icon: '🌊', desc: 'Cascading waterfall card animation' },
    { value: 'polaroid-stack', label: 'Polaroid Stack', icon: '📸', desc: 'Tilted polaroid photo style' },
    { value: 'glass-morphism', label: 'Glass Morphism', icon: '🪟', desc: 'Frosted glass translucent cards' },
    { value: 'neon-glow', label: 'Neon Glow', icon: '💡', desc: 'Dark theme with neon borders' },
    { value: 'magazine-spread', label: 'Magazine Spread', icon: '📖', desc: 'Double page magazine layout' },
    { value: 'mosaic-tiles', label: 'Mosaic Tiles', icon: '🧩', desc: 'Mixed size tile mosaic pattern' },
    { value: 'ribbon-banner', label: 'Ribbon Banner', icon: '🎀', desc: 'Cards with ribbon price tags' },
    { value: 'compact-shelf', label: 'Compact Shelf', icon: '📚', desc: 'Bookshelf-style product rows' },
    { value: 'feature-list', label: 'Feature List', icon: '📋', desc: 'Horizontal feature cards with details' },
    { value: 'hexagon-grid', label: 'Hexagon Grid', icon: '⬡', desc: 'Hexagonal honeycomb layout' },
    { value: 'gradient-wave', label: 'Gradient Wave', icon: '🌈', desc: 'Smooth gradient flowing cards' },
    { value: 'minimal-clean', label: 'Minimal Clean', icon: '✨', desc: 'Ultra minimal whitespace design' },
    { value: 'product-tiles', label: 'Product Tiles', icon: '🔲', desc: 'Square tiles with hover reveal' },
    { value: 'peek-cards', label: 'Peek Cards', icon: '👀', desc: 'Cards peeking from edge scroll' },
    { value: 'panorama-scroll', label: 'Panorama Scroll', icon: '🖼️', desc: 'Wide panoramic product cards' },
    { value: 'metro-blocks', label: 'Metro Blocks', icon: '🏙️', desc: 'Windows Metro-style blocks' },
    { value: 'catalog-list', label: 'Catalog List', icon: '📑', desc: 'Classic catalog list view' },
    { value: 'banner-carousel', label: 'Banner Carousel', icon: '🎪', desc: 'Full-width banner carousel' },
    { value: 'duo-spotlight', label: 'Duo Spotlight', icon: '🔦', desc: 'Two products spotlighted equally' },
  ];

  // Selection mode options for product source
  const selectionModeOptions = [
    { value: 'manual', label: 'Select Products Manually' },
    { value: 'category', label: 'Select by Category' },
    { value: 'subcategory', label: 'Select by Subcategory' }
  ];

  // Extended form state with new dynamic style options
  const [formData, setFormData] = useState({
    sectionName: null,
    // Section display type
    sectionType: sectionTypeOptions[0], // Default to grid-2col
    // Product Selection
    selectionMode: selectionModeOptions[0], // Default to manual
    selectedProducts: [], // For manual selection
    selectedCategory: null, // For category selection
    selectedSubcategory: null, // For subcategory selection
    // Display count - how many products to show
    displayCount: 4,
    
    // Container Styles
    containerBackground: "#f3f4f6", // Default value (hex color)
    containerBorderRadius: "rounded-xl", // Default Value


    // Header Styles
    headerTitleColor: "text-gray-900", // Default
    headerTitleFont: "text-2xl font-semibold", // Default
    headerDropShadow: "drop-shadow-sm", // Default


    // Card Styles
    cardStyle: "bg-white", //Default
    borderWidth: "border", // Default
    borderColor: "border-gray-200", // Default
    cardBorderRadius: "rounded-xl", // Default
    hoverEffect: "", // Default,
    animation: "", // Default
    cardPadding: "p-3", // Default
    cardDiscountTextColor: "text-gray-500", // Default
    imageBorderRadius: "rounded-lg", // Default
    imagePadding: "p-3", // Default

    priceDetailsLayout: priceDetailsLayoutOptions[0],
    productInfoStyle: productInfoStyleOptions[0],
  });

  const [isShowcaseModalOpen, setIsShowcaseModalOpen] = useState(false);
  const [showcaseList, setShowcaseList] = useState([]);
  const [errors, setErrors] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [currentSectionId, setCurrentSectionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [randomIndex, setRandomIndex] = useState(-1);
  
  // Product and category options for selection
  const [productOptions, setProductOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [subcategoryOptions, setSubcategoryOptions] = useState([]);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [sectionToDelete, setSectionToDelete] = useState(null);

  const randomConfigs = [
    // 1. Modern Glassmorphism
    {
      containerBackground: "bg-white/10 backdrop-blur-lg",
      containerBorderRadius: "rounded-3xl",
      headerTitleColor: "text-gray-800",
      headerTitleFont: "text-4xl font-bold",
      headerDropShadow: "drop-shadow-2xl",
      cardStyle: "bg-white/5 border border-white/20",
      borderWidth: "border-0",
      borderColor: "border-transparent",
      cardBorderRadius: "rounded-2xl",
      hoverEffect: "hover:bg-white/20 hover:backdrop-blur-sm",
      animation: "animate-fade-in",
      cardPadding: "p-3",
      cardDiscountTextColor: "text-white/70",
      imageBorderRadius: "rounded-2xl",
      imagePadding: "p-3",
      priceDetailsLayout: "side-by-side",
      productInfoStyle: "default",
    },

    // 2. Industrial Minimal
    {
      containerBackground: "bg-yellow-100",
      containerBorderRadius: "rounded-xl",
      headerTitleColor: "text-gray-800",
      headerTitleFont: "text-2xl font-semibold",
      headerDropShadow: "drop-shadow-sm",
      cardStyle: "bg-yellow-50",
      borderWidth: "border",
      borderColor: "border-gray-200",
      cardBorderRadius: "rounded-xl",
      hoverEffect: "",
      animation: "",
      cardPadding: "p-3",
      cardDiscountTextColor: "text-gray-500",
      imageBorderRadius: "rounded-lg",
      imagePadding: "p-3",
      priceDetailsLayout: "side-by-side",
      productInfoStyle: "card"
    },

    // 3. Tropical Vibes
    {
      containerBackground: "bg-green-100",
      containerBorderRadius: "rounded-3xl",
      headerTitleColor: "text-emerald-800",
      headerTitleFont: "text-3xl font-extrabold italic",
      headerDropShadow: "drop-shadow-md",
      cardStyle: "bg-white border-4 border-yellow-300",
      borderWidth: "border-4",
      borderColor: "border-yellow-300",
      cardBorderRadius: "rounded-2xl",
      hoverEffect: "hover:rotate-1 hover:shadow-xl",
      animation: "animate-wiggle",
      cardPadding: "p-4",
      cardDiscountTextColor: "text-emerald-600",
      imageBorderRadius: "rounded-2xl",
      imagePadding: "p-4",
      priceDetailsLayout: "side-by-side",
      productInfoStyle: "default",
    },

    // 4. Neon Cyber
    {
      containerBackground: "bg-purple-100",
      containerBorderRadius: "rounded-xl",
      headerTitleColor: "text-cyan-400 neon-glow",
      headerTitleFont: "text-4xl font-bold",
      headerDropShadow: "drop-shadow-[0_0_8px_rgba(34,211,238,0.6)]",
      cardStyle: "bg-black border-2 border-purple-500",
      borderWidth: "border-2",
      borderColor: "border-purple-500",
      cardBorderRadius: "rounded-lg",
      hoverEffect: "hover:border-cyan-400 hover:shadow-[0_0_15px_rgba(34,211,238,0.4)]",
      animation: "",
      cardPadding: "p-3",
      cardDiscountTextColor: "text-purple-400",
      imageBorderRadius: "rounded-lg",
      imagePadding: "p-3",
      priceDetailsLayout: "side-by-side",
      productInfoStyle: "default",
    },

    // 5. Vintage Elegance
    {
      containerBackground: "bg-pink-100",
      containerBorderRadius: "rounded-2xl",
      headerTitleColor: "text-rose-800",
      headerTitleFont: "text-3xl font-serif font-bold",
      headerDropShadow: "drop-shadow-sm",
      cardStyle: "bg-white border-2 border-pink-500",
      borderWidth: "border-2",
      borderColor: "border-pink-500",
      cardBorderRadius: "rounded-lg",
      hoverEffect: "hover:shadow-vintage",
      animation: "",
      cardPadding: "p-4",
      cardDiscountTextColor: "text-rose-600",
      imageBorderRadius: "rounded-lg",
      imagePadding: "p-0",
      priceDetailsLayout: "side-by-side",
      productInfoStyle: "default",
    },

    // 6. Minimal White
    {
      containerBackground: "bg-white",
      containerBorderRadius: "rounded-none",
      headerTitleColor: "text-black",
      headerTitleFont: "text-2xl font-light",
      headerDropShadow: "",
      cardStyle: "bg-white border",
      borderWidth: "border",
      borderColor: "border-gray-200",
      cardBorderRadius: "rounded-none",
      hoverEffect: "hover:shadow-lg",
      animation: "",
      cardPadding: "p-3",
      cardDiscountTextColor: "text-gray-500",
      imageBorderRadius: "rounded-none",
      imagePadding: "p-3",
      priceDetailsLayout: "side-by-side",
      productInfoStyle: "default",
    },

    // 7. Dark Luxury
    {
      containerBackground: "bg-gradient-to-r from-gray-900 to-black",
      containerBorderRadius: "rounded-3xl",
      headerTitleColor: "text-gold-500",
      headerTitleFont: "text-3xl font-bold",
      headerDropShadow: "drop-shadow-gold",
      cardStyle: "bg-gray-800 border-2 border-gold-500",
      borderWidth: "border-2",
      borderColor: "border-gold-500",
      cardBorderRadius: "rounded-xl",
      hoverEffect: "hover:scale-[1.02] hover:shadow-gold",
      animation: "animate-pulse",
      cardPadding: "p-3",
      cardDiscountTextColor: "text-gold-300",
      imageBorderRadius: "rounded-xl",
      imagePadding: "p-3",
      priceDetailsLayout: "stacked",
      productInfoStyle: "default",
    },

    // 8. Retro Gaming
    {
      containerBackground: "bg-blue-900",
      containerBorderRadius: "rounded-[30px]",
      headerTitleColor: "text-yellow-400",
      headerTitleFont: "text-4xl font-pixel",
      headerDropShadow: "drop-shadow-retro",
      cardStyle: "bg-red-500 border-4 border-black",
      borderWidth: "border-2",
      borderColor: "border-black",
      cardBorderRadius: "rounded-lg",
      hoverEffect: "hover:-translate-y-2",
      animation: "",
      cardPadding: "p-2",
      cardDiscountTextColor: "text-black",
      imageBorderRadius: "rounded-lg",
      imagePadding: "p-2",
      priceDetailsLayout: "side-by-side",
      productInfoStyle: "card",
    },

    // 9. Nature Inspired
    {
      containerBackground: "bg-red-100",
      containerBorderRadius: "rounded-2xl",
      headerTitleColor: "text-emerald-800",
      headerTitleFont: "text-3xl font-semibold",
      headerDropShadow: "drop-shadow-md",
      cardStyle: "bg-white border-2 border-emerald-200",
      borderWidth: "border-2",
      borderColor: "border-emerald-200",
      cardBorderRadius: "rounded-xl",
      hoverEffect: "hover:shadow-lg hover:border-emerald-400",
      animation: "",
      cardPadding: "p-4",
      cardDiscountTextColor: "text-emerald-600",
      imageBorderRadius: "rounded-xl",
      imagePadding: "p-4",
      priceDetailsLayout: "stacked",
      productInfoStyle: "default",
    },

    // 10. Futuristic Hologram
    {
      containerBackground: "bg-yellow-100",
      containerBorderRadius: "rounded-xl",
      headerTitleColor: "text-gray-900",
      headerTitleFont: "text-2xl font-semibold",
      headerDropShadow: "drop-shadow-sm",
      cardStyle: "bg-white",
      borderWidth: "border-2",
      borderColor: "border-gray-200",
      cardBorderRadius: "rounded-2xl",
      hoverEffect: "",
      animation: "none",
      cardPadding: "p-3",
      cardDiscountTextColor: "text-gray-500",
      imageBorderRadius: "rounded-2xl",
      imagePadding: "p-3",
      priceDetailsLayout: "side-by-side",
      productInfoStyle: "default"
    }
  ];

  const defaultSectionNameOptions = useMemo(() => [
    { value: "best-selling", label: "Best Selling" },
    { value: "only-for-you", label: "Only For You" },
    { value: "festival-special", label: "Festival Special" },
  ], []);

  const headerTitleColors = [
    { value: "text-white", label: "White" },
    { value: "text-gray-800", label: "Gray" },
    { value: "text-blue-800", label: "Blue" },
    { value: "text-green-800", label: "Green" },
    { value: "text-yellow-800", label: "Yellow" },
    { value: "text-red-800", label: "Red" },
    { value: "text-purple-800", label: "Purple" },
    { value: "text-pink-800", label: "Pink" },
  ];

  const containerBackgroundColors = [
    // Solid Colors
    { value: "bg-gray-50", label: "Gray 50" },
    { value: "bg-gray-100", label: "Gray 100" },
    { value: "bg-gray-200", label: "Gray 200" },
    { value: "bg-blue-50", label: "Blue 50" },
    { value: "bg-blue-100", label: "Blue 100" },
    { value: "bg-blue-200", label: "Blue 200" },
    { value: "bg-green-50", label: "Green 50" },
    { value: "bg-green-100", label: "Green 100" },
    { value: "bg-green-200", label: "Green 200" },
    { value: "bg-yellow-50", label: "Yellow 50" },
    { value: "bg-yellow-100", label: "Yellow 100" },
    { value: "bg-yellow-200", label: "Yellow 200" },
    { value: "bg-red-50", label: "Red 50" },
    { value: "bg-red-100", label: "Red 100" },
    { value: "bg-red-200", label: "Red 200" },
    { value: "bg-purple-50", label: "Purple 50" },
    { value: "bg-purple-100", label: "Purple 100" },
    { value: "bg-purple-200", label: "Purple 200" },
    { value: "bg-pink-50", label: "Pink 50" },
    { value: "bg-pink-100", label: "Pink 100" },
    { value: "bg-pink-200", label: "Pink 200" },
    { value: "bg-white", label: "White" },
    { value: "bg-transparent", label: "Transparent" },
    // Indigo & Teal & Cyan & Orange & Amber & Lime & Emerald & Rose & Fuchsia & Violet & Sky & Slate & Zinc & Stone & Neutral
    { value: "bg-indigo-50", label: "Indigo 50" },
    { value: "bg-indigo-100", label: "Indigo 100" },
    { value: "bg-indigo-200", label: "Indigo 200" },
    { value: "bg-teal-50", label: "Teal 50" },
    { value: "bg-teal-100", label: "Teal 100" },
    { value: "bg-teal-200", label: "Teal 200" },
    { value: "bg-cyan-50", label: "Cyan 50" },
    { value: "bg-cyan-100", label: "Cyan 100" },
    { value: "bg-cyan-200", label: "Cyan 200" },
    { value: "bg-orange-50", label: "Orange 50" },
    { value: "bg-orange-100", label: "Orange 100" },
    { value: "bg-orange-200", label: "Orange 200" },
    { value: "bg-amber-50", label: "Amber 50" },
    { value: "bg-amber-100", label: "Amber 100" },
    { value: "bg-amber-200", label: "Amber 200" },
    { value: "bg-lime-50", label: "Lime 50" },
    { value: "bg-lime-100", label: "Lime 100" },
    { value: "bg-lime-200", label: "Lime 200" },
    { value: "bg-emerald-50", label: "Emerald 50" },
    { value: "bg-emerald-100", label: "Emerald 100" },
    { value: "bg-emerald-200", label: "Emerald 200" },
    { value: "bg-rose-50", label: "Rose 50" },
    { value: "bg-rose-100", label: "Rose 100" },
    { value: "bg-rose-200", label: "Rose 200" },
    { value: "bg-fuchsia-50", label: "Fuchsia 50" },
    { value: "bg-fuchsia-100", label: "Fuchsia 100" },
    { value: "bg-fuchsia-200", label: "Fuchsia 200" },
    { value: "bg-violet-50", label: "Violet 50" },
    { value: "bg-violet-100", label: "Violet 100" },
    { value: "bg-violet-200", label: "Violet 200" },
    { value: "bg-sky-50", label: "Sky 50" },
    { value: "bg-sky-100", label: "Sky 100" },
    { value: "bg-sky-200", label: "Sky 200" },
    // Gradient: Warm
    { value: "bg-gradient-to-r from-red-100 to-yellow-100", label: "Warm Sunrise" },
    { value: "bg-gradient-to-r from-orange-100 to-rose-100", label: "Peach Glow" },
    { value: "bg-gradient-to-r from-amber-100 to-orange-100", label: "Golden Hour" },
    { value: "bg-gradient-to-r from-yellow-100 to-lime-100", label: "Lemon Lime" },
    { value: "bg-gradient-to-r from-red-200 to-pink-200", label: "Coral Blush" },
    { value: "bg-gradient-to-r from-orange-200 to-amber-200", label: "Autumn Glow" },
    { value: "bg-gradient-to-br from-red-100 via-orange-100 to-yellow-100", label: "Campfire" },
    { value: "bg-gradient-to-r from-pink-100 to-rose-200", label: "Rose Petal" },
    // Gradient: Cool
    { value: "bg-gradient-to-r from-blue-100 to-cyan-100", label: "Ocean Breeze" },
    { value: "bg-gradient-to-r from-cyan-100 to-teal-100", label: "Aqua Mint" },
    { value: "bg-gradient-to-r from-sky-100 to-blue-200", label: "Clear Sky" },
    { value: "bg-gradient-to-r from-teal-100 to-emerald-100", label: "Sea Green" },
    { value: "bg-gradient-to-r from-blue-200 to-indigo-200", label: "Deep Sea" },
    { value: "bg-gradient-to-br from-sky-100 via-cyan-100 to-teal-100", label: "Arctic" },
    { value: "bg-gradient-to-r from-indigo-100 to-blue-100", label: "Night Sky" },
    // Gradient: Purple & Pink
    { value: "bg-gradient-to-r from-purple-100 to-pink-100", label: "Lavender Rose" },
    { value: "bg-gradient-to-r from-violet-100 to-fuchsia-100", label: "Amethyst" },
    { value: "bg-gradient-to-r from-fuchsia-100 to-pink-100", label: "Berry Pop" },
    { value: "bg-gradient-to-r from-indigo-100 to-purple-100", label: "Royal Fade" },
    { value: "bg-gradient-to-r from-purple-200 to-violet-200", label: "Deep Violet" },
    { value: "bg-gradient-to-br from-pink-100 via-purple-100 to-indigo-100", label: "Dreamy" },
    { value: "bg-gradient-to-r from-rose-100 to-fuchsia-100", label: "Candy Floss" },
    // Gradient: Nature
    { value: "bg-gradient-to-r from-green-100 to-emerald-100", label: "Forest Light" },
    { value: "bg-gradient-to-r from-emerald-100 to-teal-100", label: "Jungle" },
    { value: "bg-gradient-to-r from-lime-100 to-green-100", label: "Fresh Spring" },
    { value: "bg-gradient-to-r from-green-100 to-lime-200", label: "Meadow" },
    { value: "bg-gradient-to-br from-green-100 via-emerald-100 to-cyan-100", label: "Rainforest" },
    // Gradient: Neutral & Luxury
    { value: "bg-gradient-to-r from-gray-100 to-gray-200", label: "Silver Mist" },
    { value: "bg-gradient-to-r from-gray-50 to-blue-50", label: "Cool Ash" },
    { value: "bg-gradient-to-r from-amber-50 to-yellow-50", label: "Warm Sand" },
    { value: "bg-gradient-to-r from-gray-100 to-zinc-200", label: "Platinum" },
    { value: "bg-gradient-to-r from-stone-100 to-amber-100", label: "Sandstone" },
    { value: "bg-gradient-to-br from-gray-100 via-stone-100 to-amber-50", label: "Desert" },
    // Gradient: Vibrant / Multicolor
    { value: "bg-gradient-to-r from-pink-100 via-purple-100 to-blue-100", label: "Cotton Candy" },
    { value: "bg-gradient-to-r from-red-100 via-yellow-100 to-green-100", label: "Rainbow Soft" },
    { value: "bg-gradient-to-r from-blue-100 via-green-100 to-yellow-100", label: "Tropical" },
    { value: "bg-gradient-to-r from-indigo-100 via-purple-100 to-pink-100", label: "Galaxy" },
    { value: "bg-gradient-to-r from-cyan-100 via-blue-100 to-violet-100", label: "Northern Light" },
    { value: "bg-gradient-to-r from-rose-100 via-amber-100 to-lime-100", label: "Sunset Garden" },
    { value: "bg-gradient-to-r from-fuchsia-100 via-rose-100 to-orange-100", label: "Fire Opal" },
    { value: "bg-gradient-to-r from-emerald-100 via-cyan-100 to-blue-100", label: "Deep Ocean" },
    { value: "bg-gradient-to-br from-violet-100 via-fuchsia-100 to-rose-100", label: "Neon Dusk" },
    { value: "bg-gradient-to-br from-sky-100 via-indigo-100 to-purple-100", label: "Twilight" },
  ];

  const borderWidths = [
    { value: "border", label: "1px (Default)" },
    { value: "border-2", label: "2px" },
    { value: "border-4", label: "4px" },
    { value: "border-8", label: "8px" },
    { value: "border-none", label: "None" },
  ];

  const borderColorOptions = [
    { value: "border-gray-200", label: "Gray 200" },
    { value: "border-gray-300", label: "Gray 300" },
    { value: "border-gray-400", label: "Gray 400" },
    { value: "border-blue-200", label: "Blue 200" },
    { value: "border-blue-300", label: "Blue 300" },
    { value: "border-blue-400", label: "Blue 400" },
    { value: "border-green-200", label: "Green 200" },
    { value: "border-green-300", label: "Green 300" },
    { value: "border-green-400", label: "Green 400" },
    { value: "border-yellow-200", label: "Yellow 200" },
    { value: "border-yellow-300", label: "Yellow 300" },
    { value: "border-yellow-400", label: "Yellow 400" },
    { value: "border-red-200", label: "Red 200" },
    { value: "border-red-300", label: "Red 300" },
    { value: "border-red-400", label: "Red 400" },
    { value: "border-purple-200", label: "Purple 200" },
    { value: "border-purple-300", label: "Purple 300" },
    { value: "border-purple-400", label: "Purple 400" },
    { value: "border-pink-200", label: "Pink 200" },
    { value: "border-pink-300", label: "Pink 300" },
    { value: "border-pink-400", label: "Pink 400" },
    { value: "border-white", label: "White" },
    { value: "border-transparent", label: "Transparent" },
  ];

  const hoverEffects = [
    { value: "hover:shadow-lg", label: "Shadow on Hover" },
    { value: "hover:scale-105", label: "Zoom In" },
    { value: "hover:scale-95", label: "Zoom Out" },
    { value: "hover:rotate-1", label: "Slight Rotation" },
    { value: "hover:translate-y-1", label: "Move Down" },
    { value: "hover:translate-y-[-1px]", label: "Move Up" },
    { value: "hover:opacity-80", label: "Dim Opacity" },
    { value: "hover:brightness-110", label: "Increase Brightness" },
    { value: "hover:contrast-125", label: "Increase Contrast" },
    { value: "", label: "None" },
  ];

  const cardStyles = [
    { value: "bg-white", label: "White" },
    { value: "bg-gray-50", label: "Light Gray" },
    { value: "bg-gray-100", label: "Gray" },
    { value: "bg-blue-50", label: "Light Blue" },
    { value: "bg-green-50", label: "Light Green" },
    { value: "bg-yellow-50", label: "Light Yellow" },
    { value: "bg-red-50", label: "Light Red" },
    { value: "bg-purple-50", label: "Light Purple" },
    { value: "bg-pink-50", label: "Light Pink" },
    { value: "bg-transparent", label: "Transparent" },
  ];

  const animations = [
    { value: "animate-bounce", label: "Bounce" },
    { value: "animate-pulse", label: "Pulse" },
    { value: "animate-fade-in", label: "Fade In" },
    { value: "animate-fade-out", label: "Fade Out" },
    { value: "animate-slide-up", label: "Slide Up" },
    { value: "animate-slide-down", label: "Slide Down" },
    { value: "animate-slide-left", label: "Slide Left" },
    { value: "animate-slide-right", label: "Slide Right" },
    { value: "animate-wiggle", label: "Wiggle" },
    { value: "animate-zoom-in", label: "Zoom In" },
    { value: "animate-zoom-out", label: "Zoom Out" },
    { value: "", label: "None" },
  ];

  // New options for Container Styles
  const containerRoundOptions = [
    { value: "rounded-none", label: "None" },
    { value: "rounded-sm", label: "Small" },
    { value: "rounded", label: "Default" },
    { value: "rounded-md", label: "Medium" },
    { value: "rounded-lg", label: "Large" },
    { value: "rounded-xl", label: "Extra Large" },
    { value: "rounded-2xl", label: "2x Extra Large" },
    { value: "rounded-3xl", label: "3x Extra Large" },
    { value: "rounded-full", label: "Full Rounded" },
  ];

  const imageRoundOptions = [
    { value: "rounded-none", label: "None" },
    { value: "rounded-sm", label: "Small" },
    { value: "rounded", label: "Default" },
    { value: "rounded-md", label: "Medium" },
    { value: "rounded-lg", label: "Large" },
    { value: "rounded-xl", label: "Extra Large" },
    { value: "rounded-2xl", label: "2x Extra Large" },
    { value: "rounded-3xl", label: "3x Extra Large" },
    { value: "rounded-full", label: "Full Rounded" },
  ];

  const imagePaddingOptions = [
    { value: "p-0", label: "None" },
    { value: "p-1", label: "Extra Small" },
    { value: "p-2", label: "Small" },
    { value: "p-3", label: "Default" },
    { value: "p-4", label: "Medium" },
    { value: "p-5", label: "Large" },
    { value: "p-6", label: "Extra Large" },
    { value: "p-8", label: "2x Extra Large" },
  ];

  // Options for card Border
  const cardRoundOptions = [
    { value: "rounded-none", label: "None" },
    { value: "rounded-sm", label: "Small" },
    { value: "rounded", label: "Default" },
    { value: "rounded-md", label: "Medium" },
    { value: "rounded-lg", label: "Large" },
    { value: "rounded-xl", label: "Extra Large" },
    { value: "rounded-2xl", label: "2x Extra Large" },
    { value: "rounded-3xl", label: "3x Extra Large" },
    { value: "rounded-full", label: "Full Rounded" },
  ];


  const cardPaddingOptions = [
    { value: "p-0", label: "None" },
    { value: "p-1", label: "Extra Small" },
    { value: "p-2", label: "Small" },
    { value: "p-3", label: "Default" },
    { value: "p-4", label: "Medium" },
    { value: "p-5", label: "Large" },
    { value: "p-6", label: "Extra Large" },
    { value: "p-8", label: "2x Extra Large" },
  ];


  const cardDiscountTextColors = [
    { value: "text-gray-400", label: "Gray 400" },
    { value: "text-gray-500", label: "Gray 500" },
    { value: "text-gray-600", label: "Gray 600" },
    { value: "text-yellow-400", label: "Yellow 400" },
    { value: "text-yellow-500", label: "Yellow 500" },
    { value: "text-yellow-600", label: "Yellow 600" },
    { value: "text-purple-400", label: "Purple 400" },
    { value: "text-purple-500", label: "Purple 500" },
    { value: "text-purple-600", label: "Purple 600" },
    { value: "text-green-400", label: "Green 400" },
    { value: "text-green-500", label: "Green 500" },
    { value: "text-green-600", label: "Green 600" },
    { value: "text-red-400", label: "Red 400" },
    { value: "text-red-500", label: "Red 500" },
    { value: "text-red-600", label: "Red 600" },
  ];

  const headerTitleFontOptions = [
    { value: "text-xl font-semibold", label: "Medium" },
    { value: "text-2xl font-semibold", label: "Large (Default)" },
    { value: "text-3xl font-bold", label: "Extra Large" },
  ]

  const headerDropShadowOptions = [
    { value: "drop-shadow-sm", label: "Small (Default)" },
    { value: "drop-shadow-md", label: "Medium" },
    { value: "drop-shadow-lg", label: "Large" },
    { value: "drop-shadow-xl", label: "Extra Large" },
    { value: "drop-shadow-2xl", label: "2x Extra Large" },
    { value: " ", label: "None" },
  ]


  const fetchShowcaseSections = useCallback(async () => {
    try {
      const data = await getShowcaseSections({ isActive: [true, false] });
      if (data.success && data.data) {
        const sections = data.data.map((section) => ({
          ...section,
          sectionName: defaultSectionNameOptions.find((opt) => opt.value === section.sectionName) || { value: section.sectionName, label: section.sectionName }, // Added fallback for sectionName,
          // Populate dynamic style options if they are stored on the backend
          containerBackground: section.containerBackground || "bg-gray-100", // Apply default if missing from backend
          containerBorderRadius: section.containerBorderRadius || "rounded-xl", // Apply default if missing from backend

          // Section display type
          sectionType: section.sectionType || "grid-2col",

          headerTitleColor: section.headerTitleColor || "text-gray-900", // Apply default if missing from backend
          headerTitleFont: section.headerTitleFont || "text-2xl font-semibold", // Apply default if missing from backend
          headerDropShadow: section.headerDropShadow || "drop-shadow-sm", // Apply default if missing from backend


          cardStyle: section.cardStyle || "bg-white", // Apply default if missing from backend
          borderWidth: section.borderWidth || "border", // Apply default if missing from backend
          borderColor: section.borderColor || "border-gray-200", // Apply default if missing from backend
          cardBorderRadius: section.cardBorderRadius || "rounded-xl", // Apply default if missing from backend
          hoverEffect: section.hoverEffect || "", // Apply default if missing from backend
          animation: section.animation || "", // Apply default if missing from backend
          cardPadding: section.cardPadding || "p-3", // Apply default if missing from backend
          cardDiscountTextColor: section.cardDiscountTextColor || "text-gray-500", // Apply default if missing from backend
          imageBorderRadius: section.imageBorderRadius || "rounded-lg", // Apply default if missing from backend
          imagePadding: section.imagePadding || "p-3", // Apply default if missing from backend
          // Product selection fields
          selectionMode: section.selectionMode || "manual",
          productIds: section.productIds || [],
          categoryId: section.categoryId || null,
          subcategoryId: section.subcategoryId || null,
        }));
        setShowcaseList(sections);
      }
    } catch (error) {
      console.error("Error fetching sections:", error);
    }
  }, [defaultSectionNameOptions]); // Add dependencies here

  // Fetch products for manual selection
  const fetchProductNames = useCallback(async () => {
    try {
      const data = await getOnlyProductNames({});
      if (data.success && data.data) {
        setProductOptions(data.data);
      }
    } catch (error) {
      console.error("Error fetching product names:", error);
    }
  }, []);

  // Fetch categories and subcategories
  const fetchCategories = useCallback(async () => {
    try {
      const data = await getCategories({ includeSubcategories: true });
      if (data.success && data.data) {
        // Build category options (parent categories only)
        const parentCategories = data.data.map((cat) => ({
          value: cat._id,
          label: cat.categoryName,
          subcategories: cat.subcategories || []
        }));
        setCategoryOptions(parentCategories);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  }, []);

  useEffect(() => {
    fetchShowcaseSections();
    fetchProductNames();
    fetchCategories();
  }, [fetchShowcaseSections, fetchProductNames, fetchCategories]);

  // Update subcategories when category changes
  useEffect(() => {
    if (formData.selectedCategory) {
      const category = categoryOptions.find(cat => cat.value === formData.selectedCategory?.value);
      if (category && category.subcategories && category.subcategories.length > 0) {
        const subcats = category.subcategories.map(sub => ({
          value: sub._id,
          label: sub.categoryName
        }));
        setSubcategoryOptions(subcats);
      } else {
        setSubcategoryOptions([]);
      }
    } else {
      setSubcategoryOptions([]);
    }
  }, [formData.selectedCategory, categoryOptions]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
    
    // Clear subcategory when category changes
    if (field === 'selectedCategory') {
      setFormData((prev) => ({ ...prev, selectedSubcategory: null }));
    }
    // Clear other selections when mode changes
    if (field === 'selectionMode') {
      setFormData((prev) => ({ 
        ...prev, 
        selectedProducts: [],
        selectedCategory: null,
        selectedSubcategory: null
      }));
    }
  };

  // Helper to get value from select or string
  const getValue = (field) => {
    if (field && typeof field === "object" && "value" in field) return field.value;
    return field || "";
  };

  const handleSaveShowcase = async () => {
    try {
      const { sectionName } = formData;
      if (!sectionName) return setErrors({ sectionName: "Section Name is required" });

      setLoading(true);
      const editingSection = isEditing
        ? showcaseList.find((section) => section._id === currentSectionId)
        : null;
      const payload = {
        sectionId: isEditing ? currentSectionId : "",
        sectionName: sectionName?.label || sectionName,

        // Section display type
        sectionType: getValue(formData.sectionType),

        // Product Selection
        selectionMode: getValue(formData.selectionMode),
        productIds: formData.selectedProducts ? formData.selectedProducts.map(p => p.value) : [],
        categoryId: formData.selectedCategory ? getValue(formData.selectedCategory) : null,
        subcategoryId: formData.selectedSubcategory ? getValue(formData.selectedSubcategory) : null,

        // Display count
        displayCount: formData.displayCount || 4,

        // Preserve section position when editing
        ...(isEditing && editingSection?.sortOrder !== undefined && editingSection?.sortOrder !== null
          ? { sortOrder: editingSection.sortOrder }
          : {}),

        // Container Styles
        containerBackground: getValue(formData.containerBackground),
        containerBorderRadius: getValue(formData.containerBorderRadius),

        // Header Styles
        headerTitleColor: getValue(formData.headerTitleColor),
        headerTitleFont: getValue(formData.headerTitleFont),
        headerDropShadow: getValue(formData.headerDropShadow),

        // Card Styles
        cardStyle: getValue(formData.cardStyle),
        borderWidth: getValue(formData.borderWidth),
        borderColor: getValue(formData.borderColor),
        cardBorderRadius: getValue(formData.cardBorderRadius),
        hoverEffect: getValue(formData.hoverEffect),
        animation: getValue(formData.animation),
        cardPadding: getValue(formData.cardPadding),
        cardDiscountTextColor: getValue(formData.cardDiscountTextColor),
        imageBorderRadius: getValue(formData.imageBorderRadius),
        imagePadding: getValue(formData.imagePadding),

        priceDetailsLayout: getValue(formData.priceDetailsLayout),
        productInfoStyle: getValue(formData.productInfoStyle),
      };
      const data = !isEditing ? await createShowcaseSection(payload) : await updateShowcaseSection(payload);
      if (data.success) {
        resetModal();
        await fetchShowcaseSections();
      }
    } catch (error) {
      console.error("Operation failed:", error);
      setErrors({ api: error.message });
    } finally {
      setLoading(false);
    }
  };

  // Handle delete showcase section
  const handleDeleteRequest = (_id) => {
    setSectionToDelete(_id);
    setConfirmModalVisible(true);
  };

  const confirmDeleteShowcase = async () => {
    try {
      setLoading(true);
      const data = await deleteShowcaseSection({ _id: sectionToDelete });
      if (data.success) {
        await fetchShowcaseSections();
      }
    } catch (error) {
      console.error("Delete failed:", error);
      setErrors({ api: error.message || "Delete failed" });
    } finally {
      setLoading(false);
      setConfirmModalVisible(false);
      setSectionToDelete(null);
    }
  };

  const handleEditShowcase = (index) => {
    const section = showcaseList[index];
    setIsEditing(true);
    setCurrentSectionId(section._id);

    // Prepare product selection data
    const selectionMode = selectionModeOptions.find(m => m.value === section.selectionMode) || selectionModeOptions[0];
    // Convert productIds to strings for comparison (handles ObjectId objects and strings)
    const productIdStrings = section.productIds 
      ? section.productIds.map(id => typeof id === 'object' ? (id.$oid || id.toString()) : String(id))
      : [];
    const selectedProducts = productIdStrings.length > 0
      ? productOptions.filter(p => productIdStrings.includes(String(p.value)))
      : [];
    // Convert categoryId to string for comparison
    const categoryIdStr = section.categoryId 
      ? (typeof section.categoryId === 'object' ? (section.categoryId.$oid || section.categoryId.toString()) : String(section.categoryId))
      : null;
    const selectedCategory = categoryIdStr 
      ? categoryOptions.find(c => String(c.value) === categoryIdStr) 
      : null;
    
    // For subcategory, need to find it from the parent category's subcategories
    let selectedSubcategory = null;
    if (section.subcategoryId && selectedCategory) {
      // Convert subcategoryId to string for comparison
      const subcategoryIdStr = typeof section.subcategoryId === 'object' 
        ? (section.subcategoryId.$oid || section.subcategoryId.toString()) 
        : String(section.subcategoryId);
      const parentCat = categoryOptions.find(c => String(c.value) === String(selectedCategory.value));
      if (parentCat && parentCat.subcategories) {
        const sub = parentCat.subcategories.find(s => String(s._id) === subcategoryIdStr);
        if (sub) {
          selectedSubcategory = { value: sub._id, label: sub.categoryName };
        }
      }
    }

    const sectionFormData = { // Create a local variable to hold the form data
      sectionName: section.sectionName || null,
      // Section display type
      sectionType: sectionTypeOptions.find(e => e.value === section.sectionType) || sectionTypeOptions[0],
      // Product selection fields
      selectionMode: selectionMode,
      selectedProducts: selectedProducts,
      selectedCategory: selectedCategory,
      selectedSubcategory: selectedSubcategory,
      // Display count
      displayCount: section.displayCount || 4,
      
      containerBackground: section.containerBackground || '#f3f4f6',
      containerBorderRadius: containerRoundOptions.find(e => e.value === section.containerBorderRadius) || containerRoundOptions[2],

      headerTitleColor: headerTitleColors.find(e => e.value === section.headerTitleColor) || headerTitleColors[1],
      headerTitleFont: headerTitleFontOptions.find(e => e.value === section.headerTitleFont) || headerTitleFontOptions[1],
      headerDropShadow: headerDropShadowOptions.find(e => e.value === section.headerDropShadow) || headerDropShadowOptions[0],

      cardStyle: cardStyles.find(e => e.value === section.cardStyle) || cardStyles[0],
      borderWidth: borderWidths.find(e => e.value === section.borderWidth) || borderWidths[0],
      borderColor: borderColorOptions.find(e => e.value === section.borderColor) || borderColorOptions[0],
      cardBorderRadius: cardRoundOptions.find(e => e.value === section.cardBorderRadius) || cardRoundOptions[2],
      hoverEffect: hoverEffects.find(e => e.value === section.hoverEffect) || hoverEffects[9],
      animation: animations.find(e => e.value === section.animation) || animations[11],
      cardPadding: cardPaddingOptions.find(e => e.value === section.cardPadding) || cardPaddingOptions[3],
      cardDiscountTextColor: cardDiscountTextColors.find(e => e.value === section.cardDiscountTextColor) || cardDiscountTextColors[1],
      imageBorderRadius: imageRoundOptions.find(e => e.value === section.imageBorderRadius) || imageRoundOptions[2],
      imagePadding: imagePaddingOptions.find(e => e.value === section.imagePadding) || imagePaddingOptions[3],

      priceDetailsLayout: priceDetailsLayoutOptions.find(e => e.value === section.priceDetailsLayout) || priceDetailsLayoutOptions[0],
      productInfoStyle: productInfoStyleOptions.find(e => e.value === section.productInfoStyle) || productInfoStyleOptions[0],
    };

    setFormData(sectionFormData); // Set the formData state with the local variable
    setIsShowcaseModalOpen(true);
  };

  const resetModal = () => {
    setIsShowcaseModalOpen(false);
    setIsEditing(false);
    setCurrentSectionId(null);
    setRandomIndex(-1);
    setFormData({
      sectionName: null,
      // Section display type
      sectionType: sectionTypeOptions[0],
      // Product selection defaults
      selectionMode: selectionModeOptions[0],
      selectedProducts: [],
      selectedCategory: null,
      selectedSubcategory: null,
      // Display count default
      displayCount: 4,
      
      containerBackground: "#f3f4f6",
      containerBorderRadius: "rounded-xl",


      headerTitleColor: "text-gray-900",
      headerTitleFont: "text-2xl font-semibold",
      headerDropShadow: "drop-shadow-sm",


      cardStyle: "bg-white",
      borderWidth: "border",
      borderColor: "border-gray-200",
      cardBorderRadius: "rounded-xl",
      hoverEffect: "",
      animation: "",
      cardPadding: "p-3",
      cardDiscountTextColor: "text-gray-500",
      imageBorderRadius: "rounded-lg",
      imagePadding: "p-3",
    });
    setErrors({});
  };

  const handleRandomize = () => {
    // Cycle through the randomConfigs array
    const newIndex = (randomIndex + 1) % randomConfigs.length;
    setRandomIndex(newIndex);

    const newConfig = randomConfigs[newIndex];

    setFormData(prev => ({
      ...prev,
      // Container Styles
      containerBackground: newConfig.containerBackground || '#f3f4f6',
      containerBorderRadius: containerRoundOptions.find(
        opt => opt.value === newConfig.containerBorderRadius
      ) || { value: "rounded-xl", label: "Default" },

      // Header Styles
      headerTitleColor: headerTitleColors.find(
        opt => opt.value === newConfig.headerTitleColor
      ) || { value: "text-gray-900", label: "Gray-900" },
      headerTitleFont: headerTitleFontOptions.find(
        opt => opt.value === newConfig.headerTitleFont
      ) || { value: "text-2xl font-semibold", label: "Large (Default)" },
      headerDropShadow: headerDropShadowOptions.find(
        opt => opt.value === newConfig.headerDropShadow
      ) || { value: "drop-shadow-sm", label: "Small (Default)" },

      // Card Styles
      cardStyle: cardStyles.find(
        opt => opt.value === newConfig.cardStyle
      ) || { value: "bg-white", label: "White" },
      borderWidth: borderWidths.find(
        opt => opt.value === newConfig.borderWidth
      ) || { value: "border", label: "1px (Default)" },
      borderColor: borderColorOptions.find(
        opt => opt.value === newConfig.borderColor
      ) || { value: "border-gray-200", label: "Gray" },
      cardBorderRadius: cardRoundOptions.find(
        opt => opt.value === newConfig.cardBorderRadius
      ) || { value: "rounded-xl", label: "Extra Large" },
      hoverEffect: hoverEffects.find(
        opt => opt.value === newConfig.hoverEffect
      ) || { value: "", label: "None" },
      animation: animations.find(
        opt => opt.value === newConfig.animation
      ) || { value: "", label: "None" },
      cardPadding: cardPaddingOptions.find(
        opt => opt.value === newConfig.cardPadding
      ) || { value: "p-3", label: "Default" },
      cardDiscountTextColor: cardDiscountTextColors.find(
        opt => opt.value === newConfig.cardDiscountTextColor
      ) || { value: "text-gray-500", label: "Gray" },
      imageBorderRadius: imageRoundOptions.find(
        opt => opt.value === newConfig.imageBorderRadius
      ) || { value: "rounded-lg", label: "Default" },
      imagePadding: imagePaddingOptions.find(
        opt => opt.value === newConfig.imagePadding
      ) || { value: "p-3", label: "Default" },
      priceDetailsLayout: priceDetailsLayoutOptions.find( // Ensure these are also updated
        opt => opt.value === newConfig.priceDetailsLayout
      ) || priceDetailsLayoutOptions[0],
      productInfoStyle: productInfoStyleOptions.find( // Ensure these are also updated
        opt => opt.value === newConfig.productInfoStyle
      ) || productInfoStyleOptions[0],
    }));
  };


  const handleToggleShowcaseStatus = async (item) => {
    try {
      let { _id, isActive, sectionName } = { ...item }
      const data = await (isActive ? deActiveShowcaseSection({ _id }) : reActiveShowcaseSection({ _id }));
      if (data.success) {
        console.log(`${sectionName} ${isActive ? 'deActivated' : 'reActivated'} successfully`);
      } else {
        console.error(`Failed to toggle:`, data.message);
      }
    } catch (error) {
      console.error("Toggle failed:", error);
      setErrors({ api: error.message });
    } finally {
      setLoading(false);
      await fetchShowcaseSections();
    }
  };

  // Move section up in order
  const handleMoveSection = async (index, direction) => {
    const newList = [...showcaseList];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newList.length) return;
    
    // Swap
    [newList[index], newList[targetIndex]] = [newList[targetIndex], newList[index]];
    
    // Update sort orders
    const orderedSections = newList.map((section, i) => ({
      _id: section._id,
      sortOrder: i
    }));
    
    try {
      setLoading(true);
      setShowcaseList(newList); // Optimistic update
      const data = await updateShowcaseSectionOrder({ orderedSections });
      if (!data.success) {
        await fetchShowcaseSections(); // Revert on failure
      }
    } catch (error) {
      console.error("Reorder failed:", error);
      await fetchShowcaseSections(); // Revert on failure
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="w-full">
      {/* Header / Add Section */}
      <div className="flex items-center space-x-2 p-4 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg shadow-md border border-gray-200 rounded-bl-none rounded-br-none border-b-0">
        <h2 className="text-xl font-bold text-gray-700 hover:text-blue-600 transition duration-300">
          Add Showcase Section
        </h2>
        <FaAngleDoubleRight className="text-blue-500 text-2xl mt-[6px]" />
        <button
          type="button"
          onClick={() => setIsShowcaseModalOpen(true)}
          className="relative flex items-center justify-center w-12 h-12 mt-[6px] bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 text-white rounded-full shadow-lg transform transition-all hover:scale-110 focus:outline-none focus:ring-4 focus:ring-blue-400"
        >
          <FiPlus className="text-3xl font-bold animate-smooth-rotate" />
        </button>
      </div>

      {/* List of Existing Sections */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-2 bg-white p-4 rounded-lg shadow-lg border border-gray-200 rounded-tl-none rounded-tr-none border-t-0">
        {showcaseList.map((section, index) => (
          <div
            key={index}
            className="group relative p-6 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow duration-200"
          >
            <div className="grid gap-6">
              <div className="space-y-2 flex-1">
                <div className="flex justify-between items-center gap-2">
                  {/* Sort Order Controls */}
                  <div className="flex flex-col gap-0.5 mr-1">
                    <button
                      onClick={() => handleMoveSection(index, 'up')}
                      disabled={index === 0 || loading}
                      className={`p-1 rounded transition-colors ${index === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:text-blue-600 hover:bg-blue-50'}`}
                      aria-label="Move up"
                    >
                      <FiArrowUp className="w-4 h-4" />
                    </button>
                    <span className="text-[10px] text-gray-400 text-center font-mono">{index + 1}</span>
                    <button
                      onClick={() => handleMoveSection(index, 'down')}
                      disabled={index === showcaseList.length - 1 || loading}
                      className={`p-1 rounded transition-colors ${index === showcaseList.length - 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:text-blue-600 hover:bg-blue-50'}`}
                      aria-label="Move down"
                    >
                      <FiArrowDown className="w-4 h-4" />
                    </button>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 line-clamp-1">
                    {section.sectionName?.label}
                  </h3>
                  <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full font-medium">
                    {sectionTypeOptions.find(t => t.value === section.sectionType)?.label || 'Grid 2 Col'}
                  </span>
                  {!section.isActive && (
                    <p className="text-red-500 text-sm font-semibold">
                      Inactive
                    </p>
                  )}
                  <div className="flex items-center gap-4">
                    {/* Edit button - Conditionally hide based on isActive */}
                    {section.isActive && (
                      <button
                        onClick={() => handleEditShowcase(index)}
                        className="p-2 bg-gray-100 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg text-blue-600 dark:text-gray-300 hover:text-blue-800 transition-colors duration-200"
                        aria-label="Edit section"
                      >
                        <FiEdit2 className="w-5 h-5" />
                      </button>
                    )}

                    {/* Delete button */}
                    <button
                      onClick={() => handleDeleteRequest(section._id)}
                      className="p-2 bg-gray-100 hover:bg-red-100 dark:hover:bg-gray-700 rounded-lg text-red-600 dark:text-gray-300 hover:text-red-800 transition-colors duration-200"
                      aria-label="Delete section"
                      disabled={loading}
                    >
                      <FiTrash2 className="w-5 h-5" />
                    </button>

                    {/* Toggle Switch */}
                    <div className="relative">
                      <input
                        type="checkbox"
                        id={`showcase-toggle-${section._id}`}
                        checked={section.isActive}
                        onChange={() => handleToggleShowcaseStatus(section)}
                        className="hidden"
                      />
                      <label
                        htmlFor={`showcase-toggle-${section._id}`}
                        className={`flex items-center cursor-pointer w-12 h-6 rounded-full p-1 transition-colors duration-300 ease-in-out ${section.isActive ? 'bg-green-500' : 'bg-gray-400'}`}
                      >
                        <span
                          className={`block w-[1.20rem] h-[1.20rem] bg-white rounded-full shadow-md m-[-2px] transform transition-transform duration-300 ease-in-out ${section.isActive ? 'translate-x-6' : 'translate-x-0'}`}
                        ></span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Confirm Delete Modal */}
      <ConfirmModal
        visible={confirmModalVisible}
        message="Are you sure you want to delete this showcase section?"
        onConfirm={confirmDeleteShowcase}
        onCancel={() => {
          setConfirmModalVisible(false);
          setSectionToDelete(null);
        }}
        cancelButtonText="Cancel"
        confirmButtonText="Delete"
      />

      {/* Modal for Adding/Editing Showcase Section */}
      <Modal
        isOpen={isShowcaseModalOpen}
        onRequestClose={resetModal}
        className="fixed inset-0 flex items-center justify-center pl-[80px] max-md:pl-0 max-md:pt-[75px] z-50"
        overlayClassName="fixed inset-0 bg-black bg-opacity-60"
        ariaHideApp={false}
        style={{
          content: {
            width: '100vw',
            height: '100vh',
            overflow: 'auto', // Changed to auto for mobile to handle content overflow
            outline: 'none',
            backgroundColor: '#fff',
            // paddingLeft: '80px',
          }
        }}
      >
        <div className="max-md:pl-0 flex flex-col md:flex-row h-full w-full"> {/* Flex container for side-by-side */}
          <div className="w-[26%] max-md:w-[100%] overflow-y-auto overflow-x-hidden md:pr-5 pb-6 p-5 max-md:h-[75%]">
            {/* Modal Header */}
            <div className="flex justify-between items-center mb-6"> {/* Increased mb for header spacing */}
              <div className="flex items-center gap-3">
                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  {isEditing ? "Edit Section" : "Add Section"}
                </h3>
                <button
                  onClick={handleRandomize}
                  className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-purple-500 hover:to-blue-500 text-white rounded-md text-sm font-medium shadow-sm transform transition-all duration-300 ease-in-out hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-600" // Adjusted button style for better look
                >
                  ✨ Magic Design
                </button>
              </div>
              <button onClick={resetModal} className="text-gray-600 dark:text-gray-300 hover:text-red-500 transition-all duration-200 font-bold hover:scale-125">
                ✕
              </button>
            </div>

            <div className="space-y-8">
              {/* Section for Random Index display - More subtle badge */}
              {randomIndex >= 0 && (
                <div className="flex justify-start"> {/* Align badge to the start */}
                  <span className="inline-flex items-center bg-blue-100 text-blue-800 text-sm font-medium px-2.5 py-1.5 rounded-full dark:bg-blue-900 dark:text-blue-300">
                    <svg aria-hidden="true" className="w-3 h-3 mr-1.5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path></svg>
                    <span>Design No. {randomIndex + 1} Applied</span>
                  </span>
                </div>
              )}

              {/* Section Name */}
              <div className="mb-3">
                <label htmlFor="sectionName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Section Name *
                </label>
                <CreatableSelect
                  id="sectionName"
                  styles={customSelect}
                  options={defaultSectionNameOptions}
                  value={formData.sectionName}
                  onChange={(option) => handleChange('sectionName', option)}
                  isClearable={false}
                  placeholder="Enter section name"
                />
                {errors.sectionName && <p className="mt-1 text-red-500 text-xs">{errors.sectionName}</p>}
              </div>

              {/* Section Type Picker - Visual Grid */}
              <section className="border-t pt-6 border-gray-200 dark:border-gray-700">
                <h4 className="block text-lg font-bold text-gray-800 dark:text-gray-200 mb-2">
                  Section Layout Type
                </h4>
                <p className="text-xs text-gray-500 mb-4">Choose how products appear on the customer's home screen</p>
                <div className="grid grid-cols-2 gap-2 max-h-[320px] overflow-y-auto pr-1">
                  {sectionTypeOptions.map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      className={`relative flex flex-col items-start p-3 rounded-xl border-2 text-left transition-all duration-200 ${
                        (formData.sectionType?.value || formData.sectionType) === type.value
                          ? 'border-blue-500 bg-blue-50 shadow-md ring-2 ring-blue-200'
                          : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                      }`}
                      onClick={() => handleChange('sectionType', type)}
                    >
                      <span className="text-xl mb-1">{type.icon}</span>
                      <span className="text-xs font-bold text-gray-800 leading-tight">{type.label}</span>
                      <span className="text-[10px] text-gray-500 leading-tight mt-0.5">{type.desc}</span>
                      {(formData.sectionType?.value || formData.sectionType) === type.value && (
                        <span className="absolute top-1.5 right-1.5 w-4 h-4 bg-blue-500 text-white rounded-full flex items-center justify-center text-[10px]">✓</span>
                      )}
                    </button>
                  ))}
                </div>
              </section>

              {/* Product Selection Section */}
              <section className="border-t pt-6 border-gray-200 dark:border-gray-700">
                <h4 className="block text-lg font-bold text-gray-800 dark:text-gray-200 mb-4">
                  Product Selection
                </h4>
                <div className="space-y-4">
                  {/* Selection Mode */}
                  <div>
                    <label htmlFor="selectionMode" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      How do you want to add products?
                    </label>
                    <Select
                      id="selectionMode"
                      styles={customSelect}
                      options={selectionModeOptions}
                      value={formData.selectionMode}
                      onChange={(option) => handleChange('selectionMode', option)}
                      isClearable={false}
                      placeholder="Select product source"
                    />
                  </div>

                  {/* Manual Product Selection */}
                  {formData.selectionMode?.value === 'manual' && (
                    <div>
                      <label htmlFor="selectedProducts" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Select Products
                      </label>
                      <Select
                        id="selectedProducts"
                        isMulti
                        closeMenuOnSelect={false}
                        styles={{
                          ...customSelect,
                          valueContainer: (provided) => ({
                            ...provided,
                            maxHeight: "120px",
                            overflowY: "auto"
                          })
                        }}
                        options={productOptions}
                        value={formData.selectedProducts}
                        onChange={(selected) => handleChange('selectedProducts', selected || [])}
                        className="react-select-container scrollbar-thin"
                        classNamePrefix="react-select"
                        placeholder="Search and select products..."
                        menuPlacement="auto"
                      />
                      {formData.selectedProducts?.length > 0 && (
                        <p className="mt-1 text-xs text-gray-500">
                          {formData.selectedProducts.length} product(s) selected
                        </p>
                      )}
                    </div>
                  )}

                  {/* Category Selection */}
                  {formData.selectionMode?.value === 'category' && (
                    <div>
                      <label htmlFor="selectedCategory" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Select Category
                      </label>
                      <Select
                        id="selectedCategory"
                        styles={customSelect}
                        options={categoryOptions}
                        value={formData.selectedCategory}
                        onChange={(option) => handleChange('selectedCategory', option)}
                        isClearable
                        placeholder="Select a category..."
                        menuPlacement="auto"
                      />
                      {formData.selectedCategory && (
                        <p className="mt-1 text-xs text-gray-500">
                          All products from "{formData.selectedCategory.label}" will be displayed
                        </p>
                      )}
                    </div>
                  )}

                  {/* Subcategory Selection */}
                  {formData.selectionMode?.value === 'subcategory' && (
                    <>
                      <div>
                        <label htmlFor="selectedCategoryForSub" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Select Parent Category
                        </label>
                        <Select
                          id="selectedCategoryForSub"
                          styles={customSelect}
                          options={categoryOptions}
                          value={formData.selectedCategory}
                          onChange={(option) => handleChange('selectedCategory', option)}
                          isClearable
                          placeholder="First, select a parent category..."
                          menuPlacement="auto"
                        />
                      </div>
                      {formData.selectedCategory && subcategoryOptions.length > 0 && (
                        <div>
                          <label htmlFor="selectedSubcategory" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Select Subcategory
                          </label>
                          <Select
                            id="selectedSubcategory"
                            styles={customSelect}
                            options={subcategoryOptions}
                            value={formData.selectedSubcategory}
                            onChange={(option) => handleChange('selectedSubcategory', option)}
                            isClearable
                            placeholder="Select a subcategory..."
                            menuPlacement="auto"
                          />
                          {formData.selectedSubcategory && (
                            <p className="mt-1 text-xs text-gray-500">
                              All products from "{formData.selectedSubcategory.label}" will be displayed
                            </p>
                          )}
                        </div>
                      )}
                      {formData.selectedCategory && subcategoryOptions.length === 0 && (
                        <p className="text-sm text-amber-600">
                          This category has no subcategories. Please select a different category or use the Category selection mode.
                        </p>
                      )}
                    </>
                  )}
                </div>
              </section>

              {/* Display Count Section */}
              <section className="border-t pt-6 border-gray-200 dark:border-gray-700">
                <h4 className="block text-lg font-bold text-gray-800 dark:text-gray-200 mb-2">
                  Products to Display
                </h4>
                <p className="text-xs text-gray-500 mb-3">How many products should appear in this section (max 20)</p>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    min="1"
                    max="20"
                    value={formData.displayCount || 4}
                    onChange={(e) => {
                      const val = Math.max(1, Math.min(20, parseInt(e.target.value) || 1));
                      handleChange('displayCount', val);
                    }}
                    className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-center text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <span className="text-sm text-gray-500">products per section</span>
                </div>
                {formData.selectionMode?.value === 'manual' && formData.selectedProducts?.length > 0 && formData.displayCount > formData.selectedProducts.length && (
                  <p className="mt-2 text-xs text-amber-600">
                    ⚠️ You selected {formData.selectedProducts.length} products but display count is {formData.displayCount}. Random products may fill remaining slots.
                  </p>
                )}
              </section>

              {/* Container Styles Section */}
              <section className="border-t pt-6 border-gray-200 dark:border-gray-700">
                <h4 className="block text-lg font-bold text-gray-800 dark:text-gray-200 mb-4">
                  Container Styling
                </h4>
                <div className="grid grid-cols-1 gap-y-4">
                  {/* Container Background - Custom Color Picker */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Background
                    </label>
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <input
                          type="color"
                          value={/^#[0-9A-Fa-f]{3,6}$/.test(formData.containerBackground) ? formData.containerBackground : '#f3f4f6'}
                          onChange={(e) => handleChange('containerBackground', e.target.value)}
                          className="h-10 w-16 rounded-lg border border-gray-300 cursor-pointer p-0.5 bg-white"
                          title="Pick a background color"
                        />
                      </div>
                      <span className="text-sm text-gray-700 font-mono bg-gray-100 px-2 py-1 rounded-md">
                        {/^#[0-9A-Fa-f]{3,6}$/.test(formData.containerBackground) ? formData.containerBackground.toUpperCase() : '#F3F4F6'}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleChange('containerBackground', '#f3f4f6')}
                        className="text-xs text-gray-400 hover:text-gray-600 underline"
                      >
                        Reset
                      </button>
                    </div>
                    <p className="mt-1 text-xs text-gray-400">Click the swatch to open the color picker</p>
                  </div>
                </div>
              </section>

              {/* Card Styles Section */}
              <section className="border-t pt-6 border-gray-200 dark:border-gray-700">
                <h4 className="block text-lg font-bold text-gray-800 dark:text-gray-200 mb-4">
                  Card Styling
                </h4>
                <div className="grid grid-cols-1 gap-y-4">
                  {/* Hover Effect */}
                  <div>
                    <label htmlFor="hoverEffect" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Hover Effect</label>
                    <Select
                      id="hoverEffect"
                      styles={customSelect}
                      options={hoverEffects}
                      value={formData.hoverEffect}
                      onChange={(option) => handleChange('hoverEffect', option || { value: '', label: 'None' })}
                      isClearable
                      placeholder="Select hover effect"
                    />
                  </div>

                  {/* Animation */}
                  <div>
                    <label htmlFor="animation" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Animation</label>
                    <Select
                      id="animation"
                      styles={customSelect}
                      options={animations}
                      value={formData.animation}
                      onChange={(option) => handleChange('animation', option || { value: '', label: 'None' })}
                      isClearable
                      placeholder="Select animation"
                    />
                  </div>
                </div>
              </section>

            </div>


            {errors.api && <p className="mt-4 text-red-500 text-sm">{errors.api}</p>}

            {/* Save Button */}
            {/* <div className="mt-8 flex justify-between gap-3">
              <button
                type="button"
                className="px-5 py-2.5 bg-gray-200 hover:bg-gray-300 rounded-md font-medium transition-colors duration-200"
                onClick={resetModal}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveShowcase}
                disabled={loading}
                className={`px-6 py-2.5 bg-gradient-to-r from-green-500 to-green-700 hover:from-green-600 hover:to-green-800 text-white rounded-md font-semibold shadow-md transition-all duration-300 ease-in-out ${loading ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105'}`} // Adjusted button style
              >
                {loading ? "Saving..." : "Save Section"}
              </button>

            </div> */}
          </div>

          <div className="w-[74%] max-md:w-[100%] bg-gray-50 rounded-r-md overflow-hidden max-md:overflow-scroll">
            <div className="flex justify-between items-center p-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Live Preview</h3>
                <p className="text-sm text-gray-500">Changes update in real-time</p>
              </div>
              <button
                onClick={handleSaveShowcase}
                disabled={loading}
                className={`px-6 py-2.5 bg-gradient-to-r from-green-500 to-green-700 hover:from-green-600 hover:to-green-800 text-white rounded-md font-semibold shadow-md transition-all duration-300 ease-in-out ${loading ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105'}`} // Adjusted button style
              >
                {loading ? "Saving..." : "Save Section"}
              </button>
            </div>
            <div className="w-full text-gray-400 font-bold">
              <DynamicBestSellingSection formData={formData} />
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ShowcaseSections;